/**
 * Pure helpers used by the search pipeline (VOY-477).
 *
 * Moved out of search-plus.tsx so both the page and the new
 * `useSearchPipeline` hook can share them. No React state, no hooks.
 */

import type { Hotel } from '@/types/travel'

// ---------------------------------------------------------------------------
// Hotel filters
// ---------------------------------------------------------------------------

export interface HotelFilters {
  priceMin?: number
  priceMax?: number
  stars?: number[]
  searchQuery?: string
  amenities?: string[]
}

/** Strip empties so the backend doesn't see meaningless filter keys. */
export function cleanFilters(filters: HotelFilters): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  if (filters.priceMin !== undefined) cleaned.priceMin = filters.priceMin
  if (filters.priceMax !== undefined) cleaned.priceMax = filters.priceMax
  if (filters.stars && filters.stars.length > 0) cleaned.stars = filters.stars
  if (filters.searchQuery && filters.searchQuery.trim()) {
    cleaned.searchQuery = filters.searchQuery.trim()
  }
  if (filters.amenities && filters.amenities.length > 0) cleaned.amenities = filters.amenities
  return cleaned
}

// ---------------------------------------------------------------------------
// City-name cleanup
// ---------------------------------------------------------------------------

const AIRPORT_KEYWORDS = [
  'airport', 'international', 'malpensa', 'heathrow', 'gatwick', 'kennedy', 'ohare', 'pistarini',
  'pleso', 'barajas', 'schiphol', 'charles de gaulle', 'fiumicino', 'ben gurion', 'narita', 'haneda',
  'logan', 'dulles', "o'hare", 'laguardia', 'jfk', 'lax', 'sfo', 'ord', 'iad', 'bwi',
]

/**
 * Strip airport-name words from a city string while preserving multi-word
 * city names. `"Milan Malpensa"` → `"Milan"`, but `"Nueva Delhi"` → `"Nueva Delhi"`.
 */
export function removeAirportName(cityName: string): string {
  if (!cityName) return cityName
  const words = cityName.split(/\s+/)
  if (words.length < 2) return cityName
  for (let i = 1; i < words.length; i++) {
    const word = words[i].toLowerCase()
    if (AIRPORT_KEYWORDS.some((keyword) => word.includes(keyword))) {
      return words.slice(0, i).join(' ')
    }
  }
  return cityName
}

// ---------------------------------------------------------------------------
// Child-age gating
// ---------------------------------------------------------------------------

interface ChildAgesContainer {
  travelers?: { children?: number; childrenAges?: number[] }
  passengers?: { children?: number; childrenAges?: number[] }
}

/**
 * RateHawk requires per-child ages when the hotel search includes children.
 * Returns true when the caller has already collected enough ages to proceed
 * without an extra UI prompt.
 */
export function hasEnoughChildAges(params: ChildAgesContainer, childCount: number): boolean {
  if (childCount <= 0) return true
  const ages = params.travelers?.childrenAges ?? params.passengers?.childrenAges
  if (!ages || ages.length < childCount) return false
  return ages.slice(0, childCount).every((age) => typeof age === 'number' && age >= 0 && age <= 17)
}

// ---------------------------------------------------------------------------
// Hotel-page cache key
// ---------------------------------------------------------------------------

export interface HotelSearchParams {
  destination?: string
  checkin?: string
  checkout?: string
  guests?: Array<{ adults: number; children: number[] }>
}

/** Returns null when the search params are incomplete (don't cache partial keys). */
export function generateHotelPageCacheKey(
  searchData: HotelSearchParams | undefined,
  filters: HotelFilters,
  page: number,
): string | null {
  if (
    !searchData?.destination ||
    !searchData?.checkin ||
    !searchData?.checkout ||
    !searchData?.guests
  ) {
    return null
  }
  const filtersKey = JSON.stringify(cleanFilters(filters))
  const guestsKey = JSON.stringify(searchData.guests)
  return `${searchData.destination}-${searchData.checkin}-${searchData.checkout}-${guestsKey}-${filtersKey}-page${page}`
}

// ---------------------------------------------------------------------------
// Cache shape + TTL
// ---------------------------------------------------------------------------

export interface HotelPageCacheEntry {
  hotels: Hotel[]
  meta: { total: number; total_pages: number }
  timestamp: number
}

export const HOTEL_CACHE_TTL_MS = 5 * 60 * 1000
export const HOTEL_CACHE_MAX_ENTRIES = 20

/** LRU-by-timestamp trim. Returns the modified map for chaining. */
export function trimHotelPageCache(
  cache: Map<string, HotelPageCacheEntry>,
): Map<string, HotelPageCacheEntry> {
  if (cache.size <= HOTEL_CACHE_MAX_ENTRIES) return cache
  const entries = Array.from(cache.entries())
  entries.sort((a, b) => a[1].timestamp - b[1].timestamp)
  for (let i = 0; i < entries.length - HOTEL_CACHE_MAX_ENTRIES; i++) {
    cache.delete(entries[i][0])
  }
  return cache
}

// ---------------------------------------------------------------------------
// Date helpers (used during hotel-fallback default dates + duration calc)
// ---------------------------------------------------------------------------

/** Day count between two dates (rounded up). Returns undefined if either missing. */
export function calculateDuration(start?: Date, end?: Date): number | undefined {
  if (!start || !end) return undefined
  const ms = Math.abs(end.getTime() - start.getTime())
  return Math.ceil(ms / 86_400_000)
}
